import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/* El ítem que pidió el buscador, para abrirlo y no sólo la pantalla.
 *
 * Igna, 02/10: «que se abra en el ítem exacto, no solo la pantalla». El
 * buscador maestro manda `?ver=<id>` en la ruta de cada resultado; cada
 * pantalla lee ese id, lo encuentra en lo que tiene cargado (o carga lo que
 * haga falta para tenerlo: otro mes, otro filtro) y lo deja a la vista.
 *
 * ── QUÉ DEVUELVE ─────────────────────────────────────────────────────────────
 *
 *   ver        el id pedido, como texto, o "" si no se pidió nada.
 *   resaltar   (el) → lo centra en la pantalla y lo hace titilar un momento.
 *              Una sola vez por id: si la lista se vuelve a dibujar (llega un
 *              cambio en vivo, se refresca) no vuelve a saltar ahí mientras la
 *              persona ya está mirando otra cosa.
 *   listo      () → saca `?ver=` de la URL sin agregar un paso al historial.
 *              Para cuando la pantalla ya abrió el ítem: si no, recargar o
 *              compartir el link lo volvería a abrir encima de lo que haya.
 *
 * ── POR QUÉ CON ATRIBUTO Y NO CON REF ───────────────────────────────────────
 *
 * Las filas se dibujan con `data-ver={id}` y se buscan con `buscarFila`. Pasar
 * un ref a cada fila de una tabla de mil es mucho cableado para encontrar UNA;
 * el atributo además se ve en el inspector y en los tests.
 */
export function useItemPedido() {
  const [params, setParams] = useSearchParams();
  const ver = params.get("ver") || "";
  const hecho = useRef("");

  useEffect(() => {
    if (!ver) hecho.current = "";
  }, [ver]);

  const resaltar = useCallback(
    (el) => {
      if (!el || !ver || hecho.current === ver) return false;
      hecho.current = ver;
      el.scrollIntoView?.({ behavior: "smooth", block: "center" });
      el.classList.remove("item-pedido");
      // Se fuerza un reflow para que la animación arranque de cero aunque la
      // clase ya hubiera estado puesta por un pedido anterior.
      void el.offsetWidth; // eslint-disable-line no-void
      el.classList.add("item-pedido");
      setTimeout(() => el.classList.remove("item-pedido"), 2600);
      return true;
    },
    [ver],
  );

  /* `listo` NO CAMBIA NUNCA. El `setSearchParams` del router se rearma cada
     vez que cambia la URL; si `listo` dependiera de él, una pantalla que lo
     tiene en las dependencias de su efecto (lo normal) lo vería cambiar al
     tocar cualquier otro parámetro —Multas, al pasar de flota— y volvería a
     pedir el ítem desde cero. Se lee el último por ref. */
  const fijar = useRef(setParams);
  fijar.current = setParams;
  const listo = useCallback(() => {
    fijar.current(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete("ver");
        return n;
      },
      { replace: true },
    );
  }, []);

  return { ver, resaltar, listo };
}

/* La fila con `data-ver` igual al id, dentro de `raiz` (o del documento). */
export function buscarFila(id, raiz) {
  if (!id) return null;
  const donde = raiz || (typeof document !== "undefined" ? document : null);
  if (!donde) return null;
  const limpio = String(id).replace(/["\\]/g, "\\$&");
  return donde.querySelector(`[data-ver="${limpio}"]`);
}
